import { useState, useMemo } from "react";
import { NumberField, Stat, ToolShell } from "./parts";
import { inr, inrShort } from "@/lib/format";

export function SipVsRealEstate() {
  const [amount, setAmount] = useState(5000000);
  const [years, setYears] = useState(10);
  const [sipReturn, setSipReturn] = useState(12);
  const [appreciation, setAppreciation] = useState(8);
  const [rentalYield, setRentalYield] = useState(3);

  const { sipCorpus, reValue, reRent, reTotal, winner, diff } = useMemo(() => {
    const sipCorpus = amount * Math.pow(1 + sipReturn / 100, years);
    const reValue = amount * Math.pow(1 + appreciation / 100, years);
    const reRent = amount * (rentalYield / 100) * years; // simple cumulative rent
    const reTotal = reValue + reRent;
    const winner = sipCorpus >= reTotal ? "SIP / Mutual Funds" : "Real Estate";
    return { sipCorpus, reValue, reRent, reTotal, winner, diff: Math.abs(sipCorpus - reTotal) };
  }, [amount, years, sipReturn, appreciation, rentalYield]);

  const sipWins = sipCorpus >= reTotal;

  return (
    <ToolShell title="SIP vs Real Estate" desc="Should the same capital go into a mutual-fund SIP or into property? Compare the projected corpus side by side.">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="space-y-8">
          <NumberField label="Capital to Invest" value={amount} onChange={setAmount} min={100000} max={200000000} step={100000} prefix="₹" testid="sip-amount" />
          <NumberField label="Horizon" value={years} onChange={setYears} min={1} max={30} step={1} suffix="yrs" testid="sip-years" />
          <NumberField label="Expected SIP Return" value={sipReturn} onChange={setSipReturn} min={4} max={20} step={0.5} suffix="%" testid="sip-return" />
          <NumberField label="Property Appreciation" value={appreciation} onChange={setAppreciation} min={0} max={20} step={0.5} suffix="%" testid="sip-appreciation" />
          <NumberField label="Rental Yield" value={rentalYield} onChange={setRentalYield} min={0} max={12} step={0.5} suffix="%" testid="sip-yield" />
        </div>
        <div className="space-y-5">
          <div className="border-2 border-foreground bg-secondary p-6" data-testid="sip-result">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Likely winner</p>
            <p className="mt-2 font-display text-3xl font-extrabold uppercase text-foreground">{winner}</p>
            <p className="mt-2 font-mono text-sm text-muted-foreground">ahead by {inrShort(diff)}</p>
          </div>
          <div className={`border p-5 ${sipWins ? "border-foreground" : "border-border"} bg-background`}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">SIP Corpus</p>
            <p className="mt-1 font-mono text-2xl font-bold text-foreground">{inr(sipCorpus)}</p>
          </div>
          <div className={`border p-5 ${!sipWins ? "border-foreground" : "border-border"} bg-background`}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Real Estate Total (value + rent)</p>
            <p className="mt-1 font-mono text-2xl font-bold text-foreground">{inr(reTotal)}</p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">Value {inrShort(reValue)} · Rent {inrShort(reRent)}</p>
          </div>
          <p className="font-mono text-[10px] text-muted-foreground">Estimates ignore taxes, loan leverage and liquidity. Real estate is illiquid; SIPs are volatile short-term.</p>
        </div>
      </div>
    </ToolShell>
  );
}
